import { Check, Loader2, Plus, Tag as TagIcon, X } from "lucide-react";
import { type CSSProperties, type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Tag, Task, TaskStatus, TaskTitleSource, TaskUpdateRequest, TenantUser } from "@/lib/project-types";
import { TASK_STATUS_LABEL_KEYS } from "@/lib/task-status";
import { cn } from "@/lib/utils";

const UNASSIGNED_ASSIGNEE_VALUE = "__project-unassigned__";
const TAG_COLOR_OPTIONS = ["gray", "red", "orange", "yellow", "green", "blue", "purple", "pink"] as const;
type TagColor = (typeof TAG_COLOR_OPTIONS)[number];
const TAG_COLOR_CLASSES: Record<TagColor, string> = {
  gray: "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-200",
  red: "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/60 dark:text-red-200",
  orange: "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-900 dark:bg-orange-950/60 dark:text-orange-200",
  yellow: "border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-900 dark:bg-yellow-950/60 dark:text-yellow-200",
  green: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200",
  blue: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/60 dark:text-blue-200",
  purple: "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950/60 dark:text-violet-200",
  pink: "border-pink-200 bg-pink-50 text-pink-700 dark:border-pink-900 dark:bg-pink-950/60 dark:text-pink-200",
};
function tagColorClass(color?: string | null): string {
  return color && color in TAG_COLOR_CLASSES ? TAG_COLOR_CLASSES[color as TagColor] : "border-border bg-muted text-muted-foreground";
}
function tagColorStyle(color?: string | null): CSSProperties | undefined {
  return color && /^#[0-9a-f]{3,8}$/i.test(color) ? { borderColor: color, color } : undefined;
}

export interface TaskFormProps {
  mode: "create" | "edit";
  status: TaskStatus;
  task?: Task;
  defaultAssigneeId?: string | null;
  tenantUsers?: TenantUser[];
  allTags?: Tag[];
  onCreateTag?: (name: string, color?: string) => Promise<Tag | null>;
  onCreateTask?: (title: string, status: TaskStatus, assignedTo?: string | null, tagIds?: string[], dueDate?: string | null, titleSource?: TaskTitleSource) => unknown | Promise<unknown>;
  dueDateSupported?: boolean;
  onUpdateTask?: (taskId: string, updates: TaskUpdateRequest) => Promise<boolean>;
  onCancel: () => void;
  onSaved: () => void;
}

/** The same fields and layout for creating and editing a kanban task. */
export function TaskForm({
  mode,
  status,
  task,
  defaultAssigneeId,
  tenantUsers = [],
  allTags = [],
  onCreateTag,
  onCreateTask,
  dueDateSupported = false,
  onUpdateTask,
  onCancel,
  onSaved,
}: TaskFormProps) {
  const { t } = useTranslation();
  const statusLabel = t(TASK_STATUS_LABEL_KEYS[status]);
  const [title, setTitle] = useState(task?.title ?? "");
  const [assignedTo, setAssignedTo] = useState(mode === "create" ? defaultAssigneeId ?? "" : task?.assigned_to ?? "");
  const assigneeTouched = useRef(false);
  useEffect(() => {
    if (mode === "create" && defaultAssigneeId && !assigneeTouched.current) setAssignedTo(defaultAssigneeId);
  }, [mode, defaultAssigneeId]);
  const [dueDate, setDueDate] = useState(task?.due_date ?? "");
  const [tagIds, setTagIds] = useState<string[]>(task?.tags.map((tag) => tag.id) ?? []);
  const [tagPopoverOpen, setTagPopoverOpen] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState<string | undefined>();
  const [creatingTag, setCreatingTag] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const activeTags = allTags.filter((tag) => !tag.is_archived);
  const toggleTag = (tagId: string) => setTagIds((current) => (
    current.includes(tagId) ? current.filter((id) => id !== tagId) : [...current, tagId]
  ));
  const createTag = async () => {
    const name = newTagName.trim();
    if (!name || !onCreateTag || creatingTag) return;
    setCreatingTag(true);
    try {
      const tag = await onCreateTag(name, newTagColor);
      if (tag) {
        setNewTagName("");
        setNewTagColor(undefined);
        setTagIds((current) => current.includes(tag.id)
          ? current : [...current, tag.id]);
      }
    } finally {
      setCreatingTag(false);
    }
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextTitle = title.trim();
    if ((mode === "edit" && !nextTitle) || saving) return;
    setSaving(true);
    setError(false);
    try {
      if (mode === "edit" && task && onUpdateTask) {
        const updates: TaskUpdateRequest = {};
        if (nextTitle !== task.title) updates.title = nextTitle;
        if ((assignedTo || null) !== task.assigned_to) {
          updates.assigned_to = assignedTo || null;
        }
        if (dueDateSupported && (dueDate || null) !== (task.due_date ?? null)) updates.due_date = dueDate || null;
        const previousTagIds = task.tags.map((tag) => tag.id);
        if (tagIds.length !== previousTagIds.length
          || tagIds.some((id) => !previousTagIds.includes(id))) {
          updates.tags = tagIds;
        }
        if (Object.keys(updates).length === 0 || await onUpdateTask(task.id, updates)) {
          onSaved();
        } else {
          setError(true);
        }
      } else if (mode === "create" && onCreateTask) {
        const result = await onCreateTask(
          nextTitle || t("projects:taskTitleFallback"),
          status,
          assignedTo || null,
          tagIds.length > 0 ? tagIds : undefined,
          ...(dueDateSupported ? [dueDate || null] as const : !nextTitle ? [undefined] as const : []),
          ...(!nextTitle ? ["default" as const] : []),
        );
        if (result === false || result === null) {
          setError(true);
        } else {
          onSaved();
        }
      }
    } catch (cause: unknown) {
      console.error("[kanban] task save failed", cause);
      setError(true);
    } finally {
      setSaving(false);
    }
  };
  return (
    <form
      className="mb-3 grid gap-3 rounded-lg border bg-background p-3"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <Input
        autoFocus
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        placeholder={t("projects:taskTitlePlaceholder")}
        aria-label={mode === "edit" ? t("projects:editTitle") : t("projects:addTaskToColumn", { status: statusLabel })}
        disabled={saving}
      />
      <label className="grid gap-1.5 text-sm font-medium text-muted-foreground">
        {t("projects:assignee")}
        <Select
          value={assignedTo || UNASSIGNED_ASSIGNEE_VALUE}
          onValueChange={(value) => {
            assigneeTouched.current = true;
            setAssignedTo(value === UNASSIGNED_ASSIGNEE_VALUE ? "" : value);
          }}
          disabled={saving}
        >
          <SelectTrigger
            aria-label={t("projects:selectAssignee")}
            title={t("projects:taskAssigneePlaceholder")}
          >
            <SelectValue placeholder={t("projects:unassigned")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNASSIGNED_ASSIGNEE_VALUE}>
              {t("projects:unassigned")}
            </SelectItem>
            {assignedTo && !tenantUsers.some((user) => user.id === assignedTo) && (
              <SelectItem value={assignedTo}>{assignedTo}</SelectItem>
            )}
            {tenantUsers.map((user) => (
              <SelectItem key={user.id} value={user.id}>
                {user.display_name ?? user.email ?? user.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      {dueDateSupported && (
        <div className="grid gap-1.5 text-sm font-medium text-muted-foreground">
          <label htmlFor="task-form-due-date">{t("projects:dueDate")}</label>
          <span className="flex items-center gap-2">
            <Input id="task-form-due-date" type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} disabled={saving} />
            {dueDate && <Button type="button" variant="ghost" size="sm" onClick={() => setDueDate("")} disabled={saving}>
              {t("projects:clearDueDate")}
            </Button>}
          </span>
        </div>
      )}
      {!dueDateSupported && <p className="text-xs text-muted-foreground" role="status">{t("projects:dueDatesUnavailable")}</p>}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">
          {t("projects:addTag")}
        </span>
        <div className="flex items-center gap-1.5">
          <Popover
            open={tagPopoverOpen}
            onOpenChange={setTagPopoverOpen}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                className={cn(
                  "h-8 w-8",
                  tagIds.length > 0 && "border-primary",
                )}
                aria-label={t("projects:addTag")}
                title={t("projects:addTag")}
                disabled={saving || (!onCreateTag && activeTags.length === 0)}
              >
                <TagIcon className="size-4" aria-hidden="true" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-2">
              <div className="flex gap-1">
                <Input
                  value={newTagName}
                  onChange={(event) => setNewTagName(event.target.value)}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void createTag();
                    }
                  }}
                  placeholder={t("projects:tagNamePlaceholder")}
                  aria-label={t("projects:createNewTag")}
                  disabled={!onCreateTag || creatingTag || saving}
                  className="h-8 text-sm"
                />
                <Button
                  type="button"
                  onClick={() => { void createTag(); }}
                  size="icon-sm"
                  className="h-8 w-8 shrink-0"
                  aria-label={t("projects:createNewTag")}
                  disabled={
                    !onCreateTag
                    || !newTagName.trim()
                    || creatingTag
                    || saving
                  }
                >
                  {creatingTag ? (
                    <Loader2 className="animate-spin" aria-hidden="true" />
                  ) : (
                    <Plus aria-hidden="true" />
                  )}
                </Button>
              </div>
              <div
                className="mt-2 flex items-center gap-1.5"
                aria-label={t("projects:tagColor")}
              >
                <span className="mr-0.5 text-[11px] text-muted-foreground">
                  {t("projects:tagColor")}
                </span>
                <button
                  type="button"
                  className={cn(
                    "size-4 rounded-full border bg-muted",
                    !newTagColor && "ring-2 ring-ring ring-offset-1",
                  )}
                  aria-label={t("projects:noTagColor")}
                  title={t("projects:noTagColor")}
                  disabled={!onCreateTag || creatingTag || saving}
                  onClick={() => setNewTagColor(undefined)}
                />
                {TAG_COLOR_OPTIONS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={cn(
                      "size-4 rounded-full border",
                      tagColorClass(color),
                      newTagColor === color && "ring-2 ring-ring ring-offset-1",
                    )}
                    aria-label={`${t("projects:tagColor")}: ${color}`}
                    title={color}
                    disabled={!onCreateTag || creatingTag || saving}
                    onClick={() => setNewTagColor(color)}
                  />
                ))}
              </div>

              <div className="mt-2 max-h-52 overflow-y-auto border-t pt-1">
                {activeTags.length === 0 ? (
                  <p className="px-2 py-2 text-xs text-muted-foreground">
                    {t("projects:noTagsYet")}
                  </p>
                ) : (
                  activeTags.map((tag) => {
                    const checked = tagIds.includes(tag.id);
                    const inherited = task?.effective_tags?.find((entry) => entry.tag.id === tag.id && entry.inherited);
                    return (
                      <Button
                        key={tag.id}
                        type="button"
                        variant="ghost"
                        size="sm"
                        role="checkbox"
                        aria-checked={checked}
                        className="h-8 w-full justify-start gap-2 px-2 text-sm font-normal"
                        disabled={saving || Boolean(inherited)}
                        title={inherited ? t("projects:subtasks.inheritedTagReadOnly", { title: inherited.source_task?.title ?? t("projects:subtasks.parentUnavailable") }) : undefined}
                        onClick={() => toggleTag(tag.id)}
                      >
                        <span
                          className={cn(
                            "grid size-4 shrink-0 place-items-center rounded-sm border",
                            checked
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-input",
                          )}
                          aria-hidden="true"
                        >
                          {checked && <Check className="size-2.5" />}
                        </span>
                        <span
                          className={cn(
                            "truncate rounded border px-1.5 py-0.5 text-xs",
                            tagColorClass(tag.color),
                          )}
                          style={tagColorStyle(tag.color)}
                        >
                          {tag.name}
                        </span>
                      </Button>
                    );
                  })
                )}
              </div>
            </PopoverContent>
          </Popover>
          {tagIds.length > 0 && (
            <Badge variant="secondary" className="h-5 min-w-5 px-1 text-[11px]">
              {tagIds.length}
            </Badge>
          )}
          {task?.effective_tags?.filter((entry) => entry.inherited).map((entry) => <Badge key={entry.tag.id} variant="outline" className="text-muted-foreground" title={t("projects:subtasks.inheritedTagSource", { title: entry.source_task?.title ?? t("projects:subtasks.parentUnavailable") })}>{entry.tag.name}</Badge>)}
        </div>
      </div>
      {error && (
        <p className="text-sm text-destructive">
          {t(mode === "edit" ? "projects:taskUpdateFailed" : "projects:taskCreateFailed")}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={saving}
        >
          <X />
          {t("projects:cancel")}
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={(mode === "edit" && !title.trim()) || saving}
        >
          {saving && <Loader2 className="animate-spin" />}
          {mode === "edit" ? t("common:actions.save") : t("projects:addTask")}
        </Button>
      </div>
    </form>
  );
}
